import { useEffect, useRef, useState } from "react";

/**
 * Ported from the Lovable build. The animation is unchanged; only the props
 * differ, because data attributes can only carry strings.
 *
 * The Lovable version took a `format` callback, which cannot cross the HTML
 * boundary - `decimals` and `separator` are declarative equivalents:
 *
 *   <?php ozria_island( 'count-up', array( 'value' => 98, 'suffix' => '%' ) ); ?>
 *   <?php ozria_island( 'count-up', array( 'value' => 2.4, 'decimals' => 1, 'prefix' => '£', 'suffix' => 'm' ) ); ?>
 */
interface CountUpProps {
  value: number | string;
  prefix?: string;
  suffix?: string;
  duration?: number | string;
  decimals?: number | string;
  /** "1" | "true" to group thousands with commas. */
  separator?: string;
}

const num = (v: number | string | undefined, fallback: number): number => {
  const parsed = typeof v === "number" ? v : parseFloat(v ?? "");
  return Number.isFinite(parsed) ? parsed : fallback;
};

const CountUp = ({
  value,
  prefix = "",
  suffix = "",
  duration = 1300,
  decimals = 0,
  separator,
}: CountUpProps) => {
  const target = num(value, 0);
  const ms = num(duration, 1300);
  const dp = Math.max(0, Math.min(6, num(decimals, 0)));
  const grouped = separator === "1" || separator === "true";

  const ref = useRef<HTMLSpanElement>(null);
  const reduced =
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const [current, setCurrent] = useState(reduced ? target : 0);

  useEffect(() => {
    if (reduced) {
      setCurrent(target);
      return;
    }
    const el = ref.current;
    let raf = 0;
    let started = false;

    const start = () => {
      if (started) return;
      started = true;
      const t0 = performance.now();
      const tick = (t: number) => {
        const p = Math.min((t - t0) / ms, 1);
        const eased = 1 - Math.pow(1 - p, 3);
        setCurrent(target * eased);
        if (p < 1) raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          start();
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    if (el) observer.observe(el);
    const fallback = window.setTimeout(start, 1200);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(raf);
      window.clearTimeout(fallback);
    };
  }, [target, ms, reduced]);

  const display = grouped
    ? current.toLocaleString("en-GB", {
        minimumFractionDigits: dp,
        maximumFractionDigits: dp,
      })
    : current.toFixed(dp);

  return (
    <span ref={ref} className="tabular">
      {prefix}
      {display}
      {suffix}
    </span>
  );
};

export default CountUp;
